import { keepPreviousData, useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { createClient } from "@/lib/connect";
import { ClickService } from "@gen/tracker/v1/click_connect";
import {
  Click,
  ClickClassification,
  type ClickJourneyStep,
  SearchClicksRequest,
} from "@gen/tracker/v1/click_pb";
import { DateRange, Pagination } from "@gen/tracker/v1/common_pb";
import { csvEscape } from "@/lib/csv";

const client = createClient(ClickService);

export const clickKeys = {
  all: ["clicks"] as const,
  search: (params: SearchClicksParams) => [...clickKeys.all, "search", params] as const,
  detail: (id: string) => [...clickKeys.all, "detail", id] as const,
  trace: (id: string) => [...clickKeys.all, "trace", id] as const,
};

export interface SearchClicksParams {
  /** ISO YYYY-MM-DD bounds, both inclusive, in `timezone` (empty = UTC days). */
  range: { from: string; to: string; timezone?: string };
  campaignId?: string;
  trafficSourceId?: string;
  landerId?: string;
  offerId?: string;
  country?: string;
  /** The visitor's city, exact. */
  city?: string;
  classification?: ClickClassification;
  /** The «Боты» tab: only visits that are not a person (bot_flag = 1). */
  botsOnly?: boolean;
  /** One traffic class (human, verified_bot, meta_crawler, …). */
  trafficClass?: string;
  /** Only visits some conversion carries — the card's «С лидом». */
  withConversions?: boolean;
  /** Count the card's switch (`segments`) too. */
  withSegments?: boolean;
  /** Free text against click_id, fbclid, gclid, clickid_external — and the IP when the term parses as an address. */
  search?: string;
  limit?: number;
  offset?: number;
}

/**
 * buildSearchClicksRequest — pure SearchClicksParams → pb request projection.
 * Split out from the hook so the field mapping is unit-testable without
 * standing up TanStack or the transport.
 */
export function buildSearchClicksRequest(params: SearchClicksParams): SearchClicksRequest {
  return new SearchClicksRequest({
    pagination: new Pagination({
      limit: params.limit ?? 50,
      offset: params.offset ?? 0,
    }),
    range: new DateRange({ from: params.range.from, to: params.range.to, timezone: params.range.timezone ?? "" }),
    campaignId: params.campaignId ?? "",
    trafficSourceId: params.trafficSourceId ?? "",
    landerId: params.landerId ?? "",
    offerId: params.offerId ?? "",
    country: params.country ?? "",
    city: params.city ?? "",
    classification: params.classification ?? ClickClassification.UNSPECIFIED,
    botsOnly: params.botsOnly ?? false,
    trafficClass: params.trafficClass ?? "",
    withConversions: params.withConversions ?? false,
    withSegments: params.withSegments ?? false,
    search: params.search ?? "",
  });
}

export function useSearchClicks(
  params: SearchClicksParams,
  options?: Partial<UseQueryOptions<SearchClicksResult>>,
) {
  return useQuery({
    queryKey: clickKeys.search(params),
    // Server pagination: keep the previous page on screen while the next one
    // loads so the table doesn't flash empty between pages.
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const res = await client.searchClicks(buildSearchClicksRequest(params));
      return {
        items: res.items,
        total: res.pagination?.total ?? res.items.length,
        classCounts: res.classCounts.map((c) => ({ trafficClass: c.trafficClass, count: Number(c.count) })),
        segments: res.segments
          ? {
              visits: Number(res.segments.visits),
              people: Number(res.segments.people),
              bots: Number(res.segments.bots),
              withConversions: Number(res.segments.withConversions),
            }
          : undefined,
      };
    },
    ...options,
  });
}

export interface SearchClicksResult {
  items: Click[];
  total: number;
  /** Visits per traffic class (only for `botsOnly`), biggest first. */
  classCounts: { trafficClass: string; count: number }[];
  /** The card's «Все · Люди · Боты · С лидом» (only for `withSegments`). */
  segments: { visits: number; people: number; bots: number; withConversions: number } | undefined;
}

/** One click by id. `lookup` is the ⌘K search: a miss is an answer, not a failure to retry. */
export function useGetClick(clickId: string | undefined, { lookup = false }: { lookup?: boolean } = {}) {
  return useQuery({
    queryKey: clickKeys.detail(clickId ?? ""),
    enabled: !!clickId,
    retry: lookup ? false : undefined,
    queryFn: async () => {
      if (!clickId) return undefined;
      const res = await client.getClick({ clickId });
      return res.click;
    },
  });
}

export function useGetClickTrace(clickId: string | undefined) {
  return useQuery({
    queryKey: clickKeys.trace(clickId ?? ""),
    enabled: !!clickId,
    queryFn: async () => {
      if (!clickId) return undefined;
      const res = await client.getClickTrace({ clickId });
      return res.trace;
    },
    // A click outside the 30-day detection-signals TTL (or pre-Phase-0) has no
    // trace row; treat the NotFound as "no trace" rather than an error toast.
    retry: false,
  });
}

/**
 * The STABLE name of a classification — what a CSV column says.
 *
 * Deliberately not the on-screen label: an export is read by a spreadsheet, a
 * reconciliation script and whoever compares this file against Keitaro, and
 * none of them should start seeing a different word because the interface
 * changed language. `classificationText` is the one that speaks to a person.
 */
export function classificationLabel(c: ClickClassification): string {
  switch (c) {
    case ClickClassification.HUMAN:
      return "Human";
    case ClickClassification.SUSPICIOUS:
      return "Suspicious";
    case ClickClassification.BOT:
      return "Bot";
    case ClickClassification.UNKNOWN:
      return "Unknown";
    default:
      return "—";
  }
}

/** What the pill in the click log says, in the operator's language. */
export function classificationText(c: ClickClassification): string {
  switch (c) {
    case ClickClassification.HUMAN:
      return "Человек";
    case ClickClassification.SUSPICIOUS:
      return "Подозрительный";
    case ClickClassification.BOT:
      return "Бот";
    case ClickClassification.UNKNOWN:
      return "Неизвестно";
    default:
      return "—";
  }
}

/** Status-pill tone classes per classification (semantic tokens only). */
export function classificationTone(c: ClickClassification): string {
  switch (c) {
    case ClickClassification.HUMAN:
      return "bg-ok/15 text-ok";
    case ClickClassification.SUSPICIOUS:
      return "bg-warn/15 text-warn";
    case ClickClassification.BOT:
      return "bg-destructive/15 text-destructive";
    case ClickClassification.UNKNOWN:
      return "bg-warn/15 text-warn";
    default:
      return "bg-muted text-muted-foreground";
  }
}

/** First non-empty external click id with its origin label. */
export function externalClickId(c: Click): { kind: string; value: string } | undefined {
  if (c.fbclid) return { kind: "fbclid", value: c.fbclid };
  if (c.gclid) return { kind: "gclid", value: c.gclid };
  if (c.clickidExternal) return { kind: "external", value: c.clickidExternal };
  return undefined;
}

// clickRowsToCsv projects the visible Click columns into an RFC-4180 CSV
// string (CRLF rows). Pure helper, mirrors campaignRowsToCsv conventions.
export function clickRowsToCsv(rows: Click[]): string {
  const header = [
    "click_id",
    "ts",
    "campaign_id",
    "country",
    "device",
    "os",
    "browser",
    "lander_id",
    "offer_id",
    "classification",
    "bot_score",
    "fbclid",
    "gclid",
    "clickid_external",
    "referer",
  ];
  const lines: string[] = [header.map(csvEscape).join(",")];
  for (const c of rows) {
    lines.push(
      [
        c.clickId,
        c.ts,
        c.campaignId,
        c.country,
        c.device,
        c.os,
        c.browser,
        c.landerId,
        c.offerId,
        classificationLabel(c.classification),
        String(c.botScore),
        c.fbclid,
        c.gclid,
        c.clickidExternal,
        c.referer,
      ]
        .map(csvEscape)
        .join(","),
    );
  }
  return lines.join("\r\n");
}

/**
 * The click's path for the drawer (доска ClickDrawer): entry, every document
 * shown, the button click, the form, the lead and each status move — oldest
 * first, from the click, step_views, CTA rows and the postback journal.
 */
export function useClickJourney(clickId: string | undefined) {
  return useQuery({
    queryKey: [...clickKeys.all, "journey", clickId ?? ""] as const,
    enabled: !!clickId,
    queryFn: async (): Promise<ClickJourneyStep[]> => (await client.getClickJourney({ clickId: clickId! })).steps,
  });
}
